import React, { useEffect, useState } from 'react';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ChecklistOutlined from '@mui/icons-material/ChecklistOutlined';
import MapOutlined from '@mui/icons-material/MapOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import InspectionChecklistModal from './InspectionChecklistModal';
import ServiceRequestCompletionModal from './ServiceRequestCompletionModal';
import { summarizeInspection, getChecklistForPpmType } from '../inspectionChecklists';
import { Modal, Button, Badge, statusVariant } from '../../../components/ui';

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString('en-GB');
};

const formatDateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB');
};

const normalizePpmType = (value) => {
  if (!value) return '';
  const v = String(value).trim().toLowerCase();
  if (v === 'weekly') return 'Weekly';
  if (v === 'monthly') return 'Monthly';
  if (v === 'quarterly') return 'Quarterly';
  if (v === 'annually' || v === 'annual' || v === 'yearly') return 'Annually';
  if (
    v === 'bi-annually' ||
    v === 'bi annually' ||
    v === 'biannual' ||
    v === 'bi-annual' ||
    v === 'bi annual' ||
    v === 'semiannual' ||
    v === 'semi-annual'
  ) {
    return 'Bi-Annually';
  }
  return String(value).trim();
};

const Field = ({ label, children, wide = false }) => (
  <div className={wide ? 'sm:col-span-2' : undefined}>
    <dt className="text-xs uppercase tracking-wide text-muted-fg">{label}</dt>
    <dd className="mt-1 break-words text-sm text-foreground">
      {children || <span className="text-muted-fg">—</span>}
    </dd>
  </div>
);

const Section = ({ title, children }) => (
  <section className="border-t border-border pt-5 first:border-t-0 first:pt-0">
    <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-fg">{title}</h4>
    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">{children}</dl>
  </section>
);

const QDFWorkOrderDetailsModal = ({
  isOpen,
  onClose,
  entry,
  onViewPdf,
  onEdit,
  onDelete,
  userRole,
  onInspectionSaved,
}) => {
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [serviceRequestOpen, setServiceRequestOpen] = useState(false);
  const [localEntry, setLocalEntry] = useState(entry);

  useEffect(() => {
    setLocalEntry(entry);
  }, [entry]);

  if (!isOpen || !entry) return null;

  const activeEntry = localEntry || entry;
  const normalizedPpmType = normalizePpmType(activeEntry.ppmType);
  const normalizedMaintenanceType =
    (activeEntry.maintenanceType || '').toString().trim().toLowerCase() ||
    (normalizedPpmType ? 'preventive' : 'corrective');
  const isPreventive = normalizedMaintenanceType === 'preventive';
  const isMonthly = isPreventive && normalizedPpmType === 'Monthly';
  const isClosed = (activeEntry.scStatus || '').toLowerCase() === 'closed';
  const hasChecklist = isPreventive && getChecklistForPpmType(normalizedPpmType).length > 0;
  const inspectionSummary = isPreventive ? summarizeInspection(activeEntry.inspection) : null;
  const inspectionCompleted = Boolean(activeEntry.inspection?.completedAt);
  const inspectionEntry = {
    ...activeEntry,
    maintenanceType: normalizedMaintenanceType,
    ppmType: normalizedPpmType || activeEntry.ppmType,
  };

  const handleInspectionSaved = (updated) => {
    setLocalEntry(updated);
    if (onInspectionSaved) onInspectionSaved(updated);
  };

  const handleViewPmMap = () => {
    window.open('/map/HIA%20Map%20PM.pdf', '_blank', 'noopener,noreferrer');
  };

  const inspectionStarted =
    inspectionSummary && inspectionSummary.pass + inspectionSummary.fail + inspectionSummary.na > 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        title={activeEntry.scNumber || 'Work order'}
        subtitle={`Created ${formatDate(activeEntry.creationDate || activeEntry.createdAt) || 'N/A'}`}
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {onDelete && userRole === 'admin' && (
                <Button
                  variant="danger-soft"
                  size="sm"
                  icon={DeleteOutline}
                  onClick={() => onDelete(activeEntry._id)}
                >
                  Delete
                </Button>
              )}
              {onEdit && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={EditOutlined}
                  onClick={() => onEdit(activeEntry)}
                >
                  Edit
                </Button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {hasChecklist && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={MapOutlined}
                    onClick={handleViewPmMap}
                  >
                    Map
                  </Button>
                  <Button
                    variant="accent"
                    size="sm"
                    icon={ChecklistOutlined}
                    onClick={() => setInspectionOpen(true)}
                  >
                    {inspectionCompleted ? 'Edit inspection' : 'Run inspection'}
                  </Button>
                </>
              )}
              {!isPreventive && (
                <Button
                  variant="accent"
                  size="sm"
                  icon={TaskAltOutlined}
                  onClick={() => setServiceRequestOpen(true)}
                >
                  Complete request
                </Button>
              )}
              <Button
                size="sm"
                icon={PictureAsPdfOutlined}
                onClick={() => onViewPdf(activeEntry)}
                disabled={!isClosed}
                title={!isClosed ? 'PDF is only available once the work order is Closed' : undefined}
              >
                View PDF
              </Button>
            </div>
          </div>
        }
      >
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <Badge variant="type" size="sm">
            {isPreventive ? 'PPM' : 'SR'}
          </Badge>
          <Badge variant="neutral" size="sm">
            {isPreventive ? normalizedPpmType || activeEntry.ppmType || 'Preventive' : 'Corrective'}
          </Badge>
          <Badge variant={statusVariant(activeEntry.scStatus)} size="sm" dot>
            {activeEntry.scStatus || 'Unknown'}
          </Badge>
        </div>

        <div className="space-y-5">
          <Section title="Overview">
            <Field label="SC number">
              <span className="tabular">{activeEntry.scNumber}</span>
            </Field>
            <Field label="Priority">{activeEntry.priority}</Field>
            <Field label="Contractor">{activeEntry.contractor}</Field>
            <Field label="Description" wide>
              {activeEntry.scDescription}
            </Field>
          </Section>

          <Section title="Dates">
            <Field label="Target response">{formatDate(activeEntry.targetResponse)}</Field>
            <Field label="Target resolution">{formatDate(activeEntry.targetResolution)}</Field>
            {!isPreventive && (
              <>
                <Field label="Actual date">{formatDate(activeEntry.actualDate)}</Field>
                <Field label="Actual resolution">{formatDate(activeEntry.actualResolution)}</Field>
              </>
            )}
          </Section>

          <Section title="Location">
            <Field label="Location">{activeEntry.location}</Field>
            {isMonthly ? (
              <Field label="Zone">{activeEntry.zone}</Field>
            ) : (
              <Field label="Department">{activeEntry.department}</Field>
            )}
          </Section>

          {!isMonthly && (
            <Section title="Asset">
              <Field label="Asset number">{activeEntry.assetNumber}</Field>
              <Field label="Asset tag">{activeEntry.assetTag}</Field>
              <Field label="Asset description" wide>
                {activeEntry.assetDescription}
              </Field>
            </Section>
          )}

          {!isPreventive && (
            <Section title="Requester">
              <Field label="Requester name">{activeEntry.requesterName}</Field>
              <Field label="Contact number">
                {activeEntry.contactNumber || activeEntry.requesterContact}
              </Field>
              <Field label="Completed by">
                {activeEntry.inspection?.completedByUser || activeEntry.inspection?.preparedBy}
              </Field>
              <Field label="Completed at">{formatDate(activeEntry.inspection?.completedAt)}</Field>
            </Section>
          )}

          {isPreventive && hasChecklist && (
            <Section title="Inspection">
              <Field label="Status">
                {inspectionCompleted ? (
                  <Badge variant="success" size="sm" dot>
                    Completed
                  </Badge>
                ) : inspectionStarted ? (
                  <Badge variant="warning" size="sm" dot>
                    In progress
                  </Badge>
                ) : (
                  <Badge variant="neutral" size="sm" dot>
                    Not started
                  </Badge>
                )}
              </Field>
              <Field label="Progress">
                {inspectionSummary ? (
                  <span className="tabular">
                    {inspectionSummary.pass + inspectionSummary.fail + inspectionSummary.na} /{' '}
                    {inspectionSummary.total} items
                  </span>
                ) : null}
              </Field>
              {inspectionSummary?.hasFailures && (
                <Field label="Failures">
                  <span className="font-medium text-danger">{inspectionSummary.fail}</span>
                </Field>
              )}
              <Field label="Prepared by">{activeEntry.inspection?.preparedBy}</Field>
              <Field label="Completed by">
                {activeEntry.inspection?.completedByUser || activeEntry.inspection?.preparedBy}
              </Field>
              <Field label="Completed at">{formatDate(activeEntry.inspection?.completedAt)}</Field>
            </Section>
          )}

          <Section title="Record">
            <Field label="Created at">{formatDateOnly(activeEntry.createdAt)}</Field>
            <Field label="Updated at">{formatDateOnly(activeEntry.updatedAt)}</Field>
          </Section>
        </div>
      </Modal>

      <InspectionChecklistModal
        isOpen={inspectionOpen}
        onClose={() => setInspectionOpen(false)}
        entry={inspectionEntry}
        readOnly={false}
        isEditMode={inspectionCompleted}
        onSaved={handleInspectionSaved}
      />

      <ServiceRequestCompletionModal
        isOpen={serviceRequestOpen}
        onClose={() => setServiceRequestOpen(false)}
        entry={activeEntry}
        readOnly={false}
        isEditMode={Boolean(activeEntry.inspection?.completedAt)}
        onSaved={handleInspectionSaved}
      />
    </>
  );
};

export default QDFWorkOrderDetailsModal;
